import _ from 'lodash'
import { pointers } from '@wix/thunderbolt-becky-types'
import { getUniqueId } from '@wix/thunderbolt-commons'
import type { Materializer, Update } from '@wix/materializer'
import type { GhostPropertiesItem } from '@wix/thunderbolt-becky-root'
import type { Component, DataMapName, QueryProperty, ViewMode } from '@wix/thunderbolt-becky-types'
import type { Unref } from '@wix/thunderbolt-catharsis'
import type { RawModel } from '../../types'
import type {
	ConsumeRefInnerParams,
	ConsumeRefInnerResult,
	DataItemWithOverridesRefs,
	DataRefsForPresetResolving,
	GetLeafsOverridesMapsRefs,
	GetOverrideDataRefs,
	GetOverrideTranslationDataRefs,
	PresetsRefsWithAncestors,
	PropertyItemWithOverrideRefs,
} from './types'
import {
	getRefCreatorInNamespace,
	getLeafsOverridesMapAspectName,
	getContextAndTemplateCompIdFromInflatedCompId,
	isRefComponent,
	isGhostPropertyItem,
} from '../byRefUtils'
import {
	DATA_MAP_NAMES,
	DATA_MAP_TO_QUERY,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
	QUERY_MAPPING,
	REF_COMPONENTS_TEMPLATE_KEY_ASPECT,
	EMPTY_SCHEMA,
	EMPTY_REMOVED,
	RESOLVED_REF_COMP_PRESETS_ASPECT,
	STRUCTURE_ASPECT,
	COMP_ID_TO_INFLATED_COMP_ID_ASPECT,
	ENVIRONMENT_NAMESPACE,
	RESOLVED_TRANSLATIONS_ASPECT,
} from '../constants'
import {
	createLayoutAndStylePresetsDependenciesResolver,
	createOverrideDataItemsResolver,
	resolveOverrideItem,
} from './dataConsumption'
import { inflateCompStructure } from './inflateCompStructure'
import { getInflatedDataItemsMap, getPresetsRefsWithAncestors, getRefCompResolvedPresetsRef } from './presetsResolving'
import { refConsumptionCompNodes } from './compNodes'
import { consumePropertyItem } from './propertyItemConsumption'
import { byRefsisDataNodes } from './dataNodes'

const RETURN_EMPTY_ARRAY_FN = () => []

export const getRefConsumptionTransformer = (rawModel: RawModel) => (value: any, [, aspect, id]: Array<string>) => {
	if (!value) {
		return value
	}

	if (aspect === RESOLVED_REF_COMP_PRESETS_ASPECT) {
		const { ownPresets, parentRefCompResolvedPresets } = value as Unref<PresetsRefsWithAncestors>
		const ownPresetsValues = ownPresets.flatMap((v) => (v?.type === 'RefArray' ? v.values : v ? [v] : []))
		return [ownPresetsValues, ...parentRefCompResolvedPresets]
	}

	if (aspect === `layoutQueryInflatedMap` || aspect === 'styleIdInflatedMap') {
		const { refArraysWithOverrides } = value as Unref<DataRefsForPresetResolving>
		if (_.last(refArraysWithOverrides)!.type !== 'RefArray') {
			return resolveOverrideItem(refArraysWithOverrides, id)
		}

		return getInflatedDataItemsMap(value, id, _.get(rawModel, [SITE_NAMESPACE, 'variants_data'], {}))
	}

	if (aspect === pointers.data.DATA_MAPS.PROPERTIES) {
		const { desktopItemWithOverrides, mobileItemWithOverrides } = value as Unref<PropertyItemWithOverrideRefs>

		const desktopPropItem = resolveOverrideItem(desktopItemWithOverrides, id)
		if (!mobileItemWithOverrides) {
			// we only create the mobile prop items array in mobile view
			return desktopPropItem
		}

		const isGhostInDesktop = isGhostPropertyItem(desktopPropItem as GhostPropertiesItem)

		// in mobile view, `id` is the propItem's inflated *mobile* id (with '-mobile' suffix)
		return isGhostInDesktop
			? {
					...desktopPropItem,
					id,
			  }
			: resolveOverrideItem(mobileItemWithOverrides, id)
	}

	if (aspect in refConsumptionCompNodes) {
		const compStructure = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, id])
		return refConsumptionCompNodes[aspect].toViewItem(compStructure, value)
	}

	if (DATA_MAP_NAMES.has(aspect as DataMapName)) {
		if (byRefsisDataNodes[aspect as DataMapName]) {
			return byRefsisDataNodes[aspect as DataMapName]?.toViewItem(id, value)
		}

		const { dataItemWithOverrides } = value as Unref<DataItemWithOverridesRefs>
		return resolveOverrideItem(dataItemWithOverrides, id)
	}

	return value
}

export const createBatchedRefConsumer = (materializer: Materializer, rawModel: RawModel, update: Update<void>) => {
	const viewMode = materializer.get<ViewMode>([ENVIRONMENT_NAMESPACE, ENVIRONMENT_NAMESPACE, 'viewMode'])

	const inflateWidgetTemplate = ({
		widgetNamespace,
		templateRootCompId,
		widgetContext,
		pageId,
		getOverrideDataRefs,
		getOverrideTranslationDataRefs,
		getLeafsOverridesMapsRefs,
	}: {
		widgetNamespace: string
		templateRootCompId: string
		widgetContext: string
		pageId: string
		getOverrideDataRefs: GetOverrideDataRefs
		getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs
		getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs
	}) => {
		const createRefInTemplateNamespace = getRefCreatorInNamespace(widgetNamespace)

		const resolveOverrideDataRefs = createOverrideDataItemsResolver({
			createRefInTemplateNamespace,
			getOverrideDataRefs,
			getOverrideTranslationDataRefs,
		})

		const resolveLayoutAndStyleRefs = createLayoutAndStylePresetsDependenciesResolver({
			createRefInTemplateNamespace,
			getOverrideDataRefs,
			getLeafsOverridesMapsRefs,
			getParentRefCompResolvedPresets: () => getRefCompResolvedPresetsRef(widgetContext),
		})

		const inflateTemplateStructure = (compId: string, isRootComp: boolean) => {
			const templateComp: Component = rawModel[widgetNamespace].structure[compId]
			if (Array.isArray(templateComp.components)) {
				for (const childId of templateComp.components) {
					inflateTemplateStructure(childId, false)
				}
			}
			const inflatedComp = inflateCompStructure({
				component: templateComp,
				parentRefCompId: widgetContext,
				pageId,
				isRootComp,
				viewMode,
			})

			if (isRefComponent(inflatedComp)) {
				const consumeResult = consumeRefInner({
					refCompId: compId,
					refCompNamespace: widgetNamespace,
					refCompContext: widgetContext,
					pageId,
					getAncestorsOverrideDataRefs: getOverrideDataRefs,
					getAncestorsOverrideTranslationDataRefs: getOverrideTranslationDataRefs,
					getAncestorsLeafsOverridesMapsRefs: getLeafsOverridesMapsRefs,
				})

				if (consumeResult?.inflatedWidgetRootCompId) {
					inflatedComp.components = [consumeResult.inflatedWidgetRootCompId]
				}
			}

			update(RESOLVED_REFS_NAMESPACE, COMP_ID_TO_INFLATED_COMP_ID_ASPECT, compId, inflatedComp.id, EMPTY_SCHEMA)
			_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, inflatedComp.id], inflatedComp)
			for (const refConsumptionCompNodeAspect in refConsumptionCompNodes) {
				const dependencies = refConsumptionCompNodes[refConsumptionCompNodeAspect].getDependencies(inflatedComp)
				update(RESOLVED_REFS_NAMESPACE, refConsumptionCompNodeAspect, inflatedComp.id, dependencies, {
					schema: dependencies,
					removed: EMPTY_REMOVED,
				})
			}

			const inflatedDataItemRefs = resolveOverrideDataRefs(templateComp)
			const dataRefsForPresetResolving = resolveLayoutAndStyleRefs(templateComp)
			const propertyItem = consumePropertyItem({
				templateComponent: templateComp,
				isMobileView: viewMode === 'mobile',
				getOverrideDataRefs,
				createRefInTemplateNamespace,
			})

			for (const dataMapName in inflatedDataItemRefs) {
				update(
					RESOLVED_REFS_NAMESPACE,
					dataMapName,
					inflatedComp[DATA_MAP_TO_QUERY[dataMapName as DataMapName]],
					inflatedDataItemRefs[dataMapName as DataMapName],
					{
						schema: inflatedDataItemRefs[dataMapName as DataMapName],
						removed: EMPTY_REMOVED,
					}
				)
			}

			for (const queryName in dataRefsForPresetResolving) {
				const aspectName = `${queryName}InflatedMap`
				update(
					RESOLVED_REFS_NAMESPACE,
					aspectName,
					inflatedComp[queryName as QueryProperty],
					dataRefsForPresetResolving[queryName as 'layoutQuery' | 'styleId'],
					{
						schema: dataRefsForPresetResolving[queryName as 'layoutQuery' | 'styleId'],
						removed: EMPTY_REMOVED,
					}
				)
			}

			update(
				RESOLVED_REFS_NAMESPACE,
				pointers.data.DATA_MAPS.PROPERTIES,
				inflatedComp[pointers.components.PROPERTY_TYPES.PROPERTY_QUERY],
				propertyItem,
				{ schema: propertyItem, removed: EMPTY_REMOVED }
			)
		}

		inflateTemplateStructure(templateRootCompId, true)
	}

	const consumeRefInner = ({
		refCompId,
		refCompNamespace,
		refCompContext,
		pageId,
		getAncestorsOverrideDataRefs,
		getAncestorsOverrideTranslationDataRefs,
		getAncestorsLeafsOverridesMapsRefs,
	}: ConsumeRefInnerParams): ConsumeRefInnerResult | null => {
		const widgetNamespace: string = materializer.get([
			refCompNamespace,
			REF_COMPONENTS_TEMPLATE_KEY_ASPECT,
			refCompId,
		])

		if (!widgetNamespace) {
			return null
		}

		// TODO: remove after internalRef implementation
		if (widgetNamespace.includes('masterPage')) {
			return null
		}

		const templateRootCompId = _.get(rawModel, [widgetNamespace, 'rootCompId'])

		const createRefInRefCompNamespace = getRefCreatorInNamespace(refCompNamespace)

		const getOverrideDataRefs: GetOverrideDataRefs = (
			compId: string,
			queryName: QueryProperty,
			itemSuffix = ''
		) => {
			const dataMapName = QUERY_MAPPING[queryName]
			const inflatedCompId = getUniqueId(refCompId, compId)

			return [
				...getAncestorsOverrideDataRefs(inflatedCompId, queryName, itemSuffix),
				createRefInRefCompNamespace(dataMapName, `${inflatedCompId}-${queryName}${itemSuffix}`),
			]
		}

		const getOverrideTranslationDataRefs: GetOverrideTranslationDataRefs = (compId: string, itemSuffix = '') => {
			const inflatedCompId = getUniqueId(refCompId, compId)

			return [
				...getAncestorsOverrideTranslationDataRefs(inflatedCompId, itemSuffix),
				createRefInRefCompNamespace(RESOLVED_TRANSLATIONS_ASPECT, `${inflatedCompId}-dataQuery${itemSuffix}`),
			]
		}

		const getLeafsOverridesMapsRefs: GetLeafsOverridesMapsRefs = (inflatedCompIdInNamespace, queryName) => {
			/**
			 * there are 2 options for `inflatedCompIdInNamespace`:
			 * 1. 	If it's the first one in the recursion, it is the template comp id.
			 * 		In this case, the context is the parentRefCompId
			 * 2.	If it's further into the recursion, it is a partially inflated comp id.
			 * 		Meaning that it is inflated up to this point (e.g. outerRefCompId_r_templateCompId).
			 * 		In this case, we first inflate it to the current level (e.g. parentRefCompId_r_outerRefCompId_r_templateCompId),
			 * 		then extract the context (parentRefCompId_r_outerRefCompId) and the templateCompId
			 */
			const inflatedCompId = getUniqueId(refCompId, inflatedCompIdInNamespace)
			const { context, templateCompId } = getContextAndTemplateCompIdFromInflatedCompId(inflatedCompId)
			return [
				...getAncestorsLeafsOverridesMapsRefs(inflatedCompId, queryName),
				createRefInRefCompNamespace(getLeafsOverridesMapAspectName(context, queryName), templateCompId),
			]
		}

		const refCompInflatedId = refCompContext ? getUniqueId(refCompContext, refCompId) : refCompId
		inflateWidgetTemplate({
			widgetNamespace,
			templateRootCompId,
			widgetContext: refCompInflatedId,
			pageId,
			getOverrideDataRefs,
			getOverrideTranslationDataRefs,
			getLeafsOverridesMapsRefs,
		})

		const templateRefComp = _.get(rawModel, [refCompNamespace, STRUCTURE_ASPECT, refCompId]) as Component
		if (templateRefComp.presetsQuery) {
			const presetsRefsWithAncestors = getPresetsRefsWithAncestors(
				templateRefComp,
				refCompContext,
				createRefInRefCompNamespace,
				getAncestorsOverrideDataRefs
			)
			update(
				RESOLVED_REFS_NAMESPACE,
				RESOLVED_REF_COMP_PRESETS_ASPECT,
				refCompInflatedId,
				presetsRefsWithAncestors,
				{
					schema: presetsRefsWithAncestors,
					removed: EMPTY_REMOVED,
				}
			)
		}

		const inflatedWidgetRootCompId = getUniqueId(refCompInflatedId, templateRootCompId)
		if (!refCompContext) {
			// The refComp is in the page, not in a widget.
			// It wasn't consumed, so its components array hasn't been populated yet
			const refComp = _.get(rawModel, [SITE_NAMESPACE, STRUCTURE_ASPECT, refCompId]) as Component
			const refCompWithChildId = {
				...refComp,
				components: [inflatedWidgetRootCompId],
			}

			_.set(rawModel, [RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, refCompId], refCompWithChildId)
			update(RESOLVED_REFS_NAMESPACE, STRUCTURE_ASPECT, refCompId, refCompWithChildId, EMPTY_SCHEMA)
		}

		return {
			inflatedWidgetRootCompId,
		}
	}

	return ({ refCompNamespace, refCompId }: Pick<ConsumeRefInnerParams, 'refCompNamespace' | 'refCompId'>) => {
		const pageId = _.get(rawModel, [refCompNamespace, STRUCTURE_ASPECT, refCompId, 'metaData', 'pageId'])
		consumeRefInner({
			refCompId,
			refCompNamespace,
			refCompContext: '',
			pageId,
			getAncestorsOverrideDataRefs: RETURN_EMPTY_ARRAY_FN,
			getAncestorsOverrideTranslationDataRefs: RETURN_EMPTY_ARRAY_FN,
			getAncestorsLeafsOverridesMapsRefs: RETURN_EMPTY_ARRAY_FN,
		})
	}
}
